import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { SkeletonRows, EmptyGoodNews, NoResults, ErrorBanner } from '../shell/carbonStates';
import { CARBON_CHIP, CARBON_TH_TEXT, carbonFilterChip } from './carbonClasses';

// ============================================================================
// CHA-283 — WorklistTable: the canonical worklist/table primitive.
// ============================================================================
// ONE table grammar for every dense-list surface (UI/UX review priority #1) —
// the Orders reference anatomy (design README §6) extracted into a reusable
// component so no surface ever gets a one-off table again. STRICTLY
// presentation-only: it renders the rows, filter chips, states, pagination and
// drawer CHROME that callers pass in; it never owns compliance logic, never
// calls Firestore/functions, and never invents data. Callers keep their
// callables, gating and confirm flows and pass them down as row actions /
// drawer content.
//
// Pattern sources: AgentsCockpit queue grid (CHA-273 — severity rail, mono
// metric column, filter chips w/ live counts, blue-10 selection),
// OrdersDashboard CarbonPoTable (CHA-274 — filter bar, mono header on
// gray-10, pagination footer, drawer a11y: Escape + focus restore),
// carbonStates (CHA-272 — the six states; honest error > fabricated
// "all clear").
//
// See WorklistTable.md (next to this file) for the prop reference + usage.

const DEFAULT_PAGE_SIZE = 10;

export default function WorklistTable({
  // Cells: [{ key, label?, width?, align?, render(row) }]. Header row renders
  // only if some column has a label (or statusChipOf adds its STATUS column).
  columns = [],
  rows = [],
  rowKey,
  // Optional data-testid stamped on every row (consumers keep their existing
  // test hooks, e.g. the cockpit's "queue-row").
  rowTestId,
  // Optional filter bar attached to the table top:
  // { title?, search?: { placeholder, searchFn(row, q) },
  //   chips?: [{ key, label }] ('all' = match everything), chipOf(row) → key,
  //   activeChip? + onChipChange?(key)  → controlled (e.g. hash-synced) —
  //     omit activeChip for internal state,
  //   showChipCounts? (default true), emptyAsGoodNews? (default false — when
  //   true, a chip-filtered empty renders `empty` instead of NoResults; use it
  //   where an empty filter slice IS good news, e.g. an agent queue) }
  filterBar,
  // (row) → CSS color for the 3px left severity rail, or null for none.
  severityOf,
  // (row) → bordered mono chip descriptor { label, icon?, className?, ink?,
  // border? } or null. When provided, a trailing STATUS column is appended.
  statusChipOf,
  onRowClick,
  // Controlled selection: the row whose rowKey matches gets the blue-10
  // selected background, and (with `drawer`) the drawer opens for it.
  selectedKey = null,
  // Client-side pagination: true | { pageSize } — off by default (short lists).
  pagination = false,
  // Reconciling-counts slot under the table: node, or (filteredRows) → node.
  footer,
  loading = false,
  loadingRows = 5,
  // string | { what?, staleNote?, onRetry?, emptyText? }. A banner renders iff
  // `what` is set (pass no `what` when the caller owns its own page-level
  // banner). With error + zero rows the body NEVER fabricates good news — it
  // renders `emptyText` instead (TodayHome honesty pattern).
  error = null,
  // True-empty (no rows, no error) → EmptyGoodNews: { title?, detail? }.
  empty,
  // Drawer slot — the primitive owns the CHROME (overlay, backdrop, slide-in
  // panel, Escape, focus restore per the CHA-274 a11y pattern); the CALLER
  // renders the content: { render(row, { close }), onClose(), width?=480,
  // ariaLabelOf?(row), closeDisabled? (blocks backdrop + Escape while an
  // action is in flight) }.
  drawer,
  // Horizontal-scroll floor for wide tables (e.g. '1080px').
  minWidth,
}) {
  const [query, setQuery] = useState('');
  const [chipState, setChipState] = useState('all');
  const [page, setPage] = useState(0);

  const err = !error ? null : typeof error === 'string' ? { what: error } : error;
  const search = filterBar?.search || null;
  const chips = filterBar?.chips || null;
  const chipOf = filterBar?.chipOf;
  const chipControlled = filterBar?.activeChip !== undefined;
  const activeChip = chipControlled ? filterBar.activeChip : chipState;
  const showChipCounts = filterBar?.showChipCounts !== false;

  const setChip = (key) => {
    if (!chipControlled) setChipState(key);
    filterBar?.onChipChange?.(key);
    setPage(0);
  };
  const clearFilters = () => {
    setQuery('');
    setChip('all');
    setPage(0);
  };

  const q = query.trim().toLowerCase();
  const chipMatch = (r) => !chips || !chipOf || activeChip === 'all' || chipOf(r) === activeChip;
  const filtered = rows.filter((r) => (!q || !search || search.searchFn(r, q)) && chipMatch(r));
  const chipCount = (key) => (key === 'all' ? rows.length : rows.filter((r) => chipOf(r) === key).length);

  const pageSize = pagination ? (pagination.pageSize || DEFAULT_PAGE_SIZE) : 0;
  const pageCount = pagination ? Math.max(1, Math.ceil(filtered.length / pageSize)) : 1;
  const pageClamped = pagination ? Math.min(page, pageCount - 1) : 0;
  const visible = pagination ? filtered.slice(pageClamped * pageSize, (pageClamped + 1) * pageSize) : filtered;

  const gridTemplate = [
    ...columns.map((c) => c.width || 'minmax(0,1fr)'),
    statusChipOf ? 'max-content' : null,
    onRowClick ? '18px' : null,
  ]
    .filter(Boolean)
    .join(' ');
  const hasHeader = columns.some((c) => c.label) || !!statusChipOf;

  const selectedRow =
    selectedKey != null ? rows.find((r) => rowKey(r) === selectedKey) || null : null;

  // When the selected row leaves `rows` (resolved / archived / snapshot
  // removal), tell the caller so its stale selection key is cleared — else a
  // later re-appearance of that key (e.g. "Show N certified") would pop the
  // drawer open unbidden (§6.3 self-review). Refs keep the mount-cheap effect
  // from refiring on inline-object prop identity churn.
  const staleSelection = !!drawer && selectedKey != null && !selectedRow;
  const drawerCloseRef = useRef(null);
  drawerCloseRef.current = drawer?.onClose || null;
  useEffect(() => {
    if (staleSelection && drawerCloseRef.current) drawerCloseRef.current();
  }, [staleSelection]);

  const showSkeleton = loading && rows.length === 0;
  const trueEmpty = !showSkeleton && rows.length === 0;
  const filteredEmpty = !showSkeleton && rows.length > 0 && filtered.length === 0;
  // A chip-filtered empty can be good news (an agent queue at zero); a
  // SEARCH-filtered empty never is — always NoResults with the way out.
  const emptyGood = <EmptyGoodNews title={empty?.title} detail={empty?.detail} />;
  // With an error in play, ANY empty slice is unknown, not good news.
  const errEmpty = err ? (
    <div className="px-[18px] py-8 text-center text-xs text-carbon-gray-50">
      {err.emptyText || 'Data unavailable.'}
    </div>
  ) : null;

  return (
    <div className="bg-carbon-white border border-carbon-border">
      {err?.what ? (
        <div className="px-[18px] pt-3">
          <ErrorBanner what={err.what} staleNote={err.staleNote} onRetry={err.onRetry} />
        </div>
      ) : null}

      {/* Dim only the table content while refreshing over stale rows — the
          drawer must stay outside this subtree (opacity would flash it AND
          demote its z-index into a local stacking context). */}
      <div className={loading && rows.length > 0 ? 'opacity-60' : ''}>
      {filterBar ? (
        <div className="flex items-center justify-between flex-wrap gap-x-3 border-b border-carbon-border">
          {filterBar.title ? (
            <div className="text-[15px] font-semibold pl-[18px] py-3.5">{filterBar.title}</div>
          ) : null}
          {search ? (
            <div className="flex items-center gap-2 px-3.5 py-2 flex-1 min-w-[200px]">
              <Search size={15} className="text-carbon-gray-50 flex-shrink-0" />
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(0);
                }}
                placeholder={search.placeholder}
                className="border-none outline-none text-[13px] flex-1 bg-transparent text-carbon-ink placeholder:text-carbon-gray-40 min-w-0"
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => {
                    // Clear ONLY the search — an active chip survives.
                    setQuery('');
                    setPage(0);
                  }}
                  aria-label="Clear search"
                  className="bg-transparent border-none cursor-pointer text-carbon-gray-50 hover:text-carbon-ink flex p-0.5"
                >
                  <X size={14} />
                </button>
              ) : null}
            </div>
          ) : null}
          {chips ? (
            <div className="flex items-center gap-1.5 flex-wrap px-[18px] py-2">
              {chips.map((c) => {
                const on = activeChip === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setChip(c.key)}
                    aria-pressed={on}
                    className={carbonFilterChip(on)}
                  >
                    {c.label}
                    {showChipCounts && chipOf ? ` ${chipCount(c.key)}` : ''}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <div style={minWidth ? { minWidth } : undefined}>
          {hasHeader ? (
            <div
              className="grid gap-3 px-[18px] py-2.5 border-b border-carbon-border bg-carbon-gray-10 items-center"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              {columns.map((c) => (
                <span
                  key={c.key}
                  className={`${CARBON_TH_TEXT} ${c.align === 'right' ? 'text-right' : ''}`}
                >
                  {c.label || ''}
                </span>
              ))}
              {statusChipOf ? <span className={CARBON_TH_TEXT}>Status</span> : null}
              {onRowClick ? <span /> : null}
            </div>
          ) : null}

          {showSkeleton ? (
            <SkeletonRows rows={loadingRows} />
          ) : trueEmpty && err ? (
            // Zero rows while errored is UNKNOWN, not good news — never
            // fabricate "queue clear" over a failed load.
            errEmpty
          ) : trueEmpty ? (
            emptyGood
          ) : filteredEmpty ? (
            err ? (
              // An empty filter slice over an errored feed is unknown too.
              errEmpty
            ) : !q && filterBar?.emptyAsGoodNews ? (
              emptyGood
            ) : (
              <NoResults query={q ? query.trim() : undefined} onClear={clearFilters} />
            )
          ) : (
            visible.map((r) => {
              const key = rowKey(r);
              const sev = severityOf ? severityOf(r) : null;
              const isSel = selectedKey != null && key === selectedKey;
              const clickable = !!onRowClick;
              return (
                <div
                  key={key}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  data-testid={rowTestId}
                  onClick={clickable ? () => onRowClick(r) : undefined}
                  onKeyDown={
                    clickable
                      ? (e) => {
                          // Enter/Space activate the row itself — never steal
                          // keystrokes from interactive cell content.
                          if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                            e.preventDefault();
                            onRowClick(r);
                          }
                        }
                      : undefined
                  }
                  className={`relative grid gap-3 items-center px-[18px] py-3 border-t border-carbon-gray-10 text-left ${
                    isSel
                      ? 'bg-carbon-blue-10'
                      : `bg-carbon-white ${clickable ? 'hover:bg-carbon-gray-10 cursor-pointer' : ''}`
                  }`}
                  style={{ gridTemplateColumns: gridTemplate }}
                >
                  {sev ? (
                    <span
                      data-testid="worklist-severity-rail"
                      className="absolute left-0 top-0 bottom-0 w-[3px]"
                      style={{ background: sev }}
                    />
                  ) : null}
                  {columns.map((c) => (
                    <div key={c.key} className={`min-w-0 ${c.align === 'right' ? 'text-right' : ''}`}>
                      {c.render(r)}
                    </div>
                  ))}
                  {statusChipOf ? <StatusCell chip={statusChipOf(r)} /> : null}
                  {clickable ? <ChevronRight size={16} className="text-carbon-gray-30" /> : null}
                </div>
              );
            })
          )}

          {pagination && !showSkeleton && !trueEmpty ? (
            <div className="flex items-center justify-between px-[18px] py-2 border-t border-carbon-border bg-carbon-gray-10">
              <span className="font-plex-mono text-[11px] text-carbon-gray-50">
                {filtered.length === 0
                  ? '0 RESULTS'
                  : `${pageClamped * pageSize + 1}–${Math.min(
                      filtered.length,
                      (pageClamped + 1) * pageSize
                    )} OF ${filtered.length}`}
              </span>
              <div className="flex gap-px">
                <button
                  type="button"
                  onClick={() => setPage(Math.max(0, pageClamped - 1))}
                  aria-label="Previous page"
                  disabled={pageClamped === 0}
                  aria-disabled={pageClamped === 0}
                  className={`w-8 h-8 flex items-center justify-center bg-carbon-white border border-carbon-border ${
                    pageClamped > 0 ? 'text-carbon-ink hover:bg-carbon-gray-10' : 'text-carbon-gray-30 cursor-default'
                  }`}
                >
                  <ChevronLeft size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => setPage(Math.min(pageCount - 1, pageClamped + 1))}
                  aria-label="Next page"
                  disabled={pageClamped >= pageCount - 1}
                  aria-disabled={pageClamped >= pageCount - 1}
                  className={`w-8 h-8 flex items-center justify-center bg-carbon-white border border-carbon-border ${
                    pageClamped < pageCount - 1
                      ? 'text-carbon-ink hover:bg-carbon-gray-10'
                      : 'text-carbon-gray-30 cursor-default'
                  }`}
                >
                  <ChevronRight size={15} />
                </button>
              </div>
            </div>
          ) : null}

          {footer ? (
            <div className="border-t border-carbon-border">
              {typeof footer === 'function' ? footer(filtered) : footer}
            </div>
          ) : null}
        </div>
      </div>
      </div>

      {drawer && selectedRow ? <WorklistDrawer row={selectedRow} drawer={drawer} /> : null}
    </div>
  );
}

// Trailing bordered mono status chip (statusChipOf descriptor).
function StatusCell({ chip }) {
  if (!chip) return <span />;
  const Icon = chip.icon;
  return (
    <span
      data-testid="worklist-status-chip"
      className={`inline-flex w-fit items-center gap-1 whitespace-nowrap ${CARBON_CHIP} ${chip.className || ''}`}
      style={chip.ink ? { color: chip.ink, borderColor: chip.border || chip.ink } : undefined}
    >
      {Icon ? <Icon size={11} /> : null}
      {chip.label}
    </span>
  );
}

// Drawer CHROME (CHA-274 a11y pattern): fixed overlay + backdrop, right
// slide-over panel, Escape-to-close, focus lands on the panel on open and
// returns to the previously-focused element (the row) on unmount. Backdrop +
// Escape are disabled while `closeDisabled` (action in flight). Content is
// 100% caller-rendered — the panel is a plain flex-col the caller fills
// (header / scrollable body / footer).
function WorklistDrawer({ row, drawer }) {
  const { render, onClose, closeDisabled = false, width = 480, ariaLabelOf } = drawer;
  const panelRef = useRef(null);
  // Latest close handler/gate via ref so the mount effect never re-runs
  // (re-running would re-steal focus mid-interaction).
  const liveRef = useRef({ onClose, closeDisabled });
  liveRef.current = { onClose, closeDisabled };

  useEffect(() => {
    const prev = document.activeElement;
    panelRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape' && !liveRef.current.closeDisabled) liveRef.current.onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex justify-end font-plex text-carbon-ink">
      <style>{'@keyframes ctoWorklistDrawerIn { from { transform: translateX(40px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }'}</style>
      <div
        data-testid="worklist-drawer-backdrop"
        className="absolute inset-0 bg-[rgba(22,22,22,0.5)]"
        onClick={closeDisabled ? undefined : onClose}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabelOf ? ariaLabelOf(row) : 'Details'}
        className="relative w-full h-full bg-carbon-gray-10 flex flex-col outline-none"
        style={{
          maxWidth: width,
          boxShadow: '-24px 0 60px rgba(22,22,22,0.25)',
          animation: 'ctoWorklistDrawerIn 0.25s ease both',
        }}
      >
        {render(row, { close: onClose })}
      </div>
    </div>
  );
}
